import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Multiselect from "multiselect-react-dropdown";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import { Input, Row, Col, Card, CardBody, Button, Spinner } from "reactstrap";
import "../../../App.css"
import { uploadCourseIcon } from "../../../slices/thunks";
import { toast } from "react-toastify"; // Added import for toast
import { clearUploadedCourseImageUrl } from "../../../slices/courses/reducer";

// Helper function to format file sizes
function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

const AddCourseForm = ({
    labels,
    formikProps,
    isAddOrEdit,
    onTranslateClickHandler,
    translationLoading,
    lastTranslatedField,
}) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isRTL = i18n.dir() === "rtl";

    const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formikProps;

    const today = new Date().toISOString().split("T")[0];

    // Selectors for data needed by dropdowns, etc.
    const { ReferralReward, Days, imageLoad, uploadedImageUrlFromStore } = useSelector((state) => ({
        ReferralReward: state.ReferralReward.referralReward, // Assuming structure
        Days: state.Common.days, // Assuming structure
        uploadedImageUrlFromStore: state.Courses.uploadedImageUrl, // Corrected: Should be from Courses slice
        imageLoad: state.Courses.imageLoad,                     // Corrected: Should be from Courses slice
    }));
    
    const courseIconFieldName = useMemo(() => labels.find(l => l.fieldName === 'file')?.name, [labels]);

    // This local state is for managing the preview URL for new files to avoid re-creating it on every render
    // and to properly revoke it.
    const [filePreviews, setFilePreviews] = useState({});

    useEffect(() => {
        // When a new image URL is available from Redux store (after upload)
        if (uploadedImageUrlFromStore && courseIconFieldName && values[courseIconFieldName] instanceof File) {
            setFieldValue(courseIconFieldName, uploadedImageUrlFromStore);
            dispatch(clearUploadedCourseImageUrl()); 
        }
    }, [uploadedImageUrlFromStore, dispatch, setFieldValue, values, courseIconFieldName]);

    useEffect(() => {
        // Cleanup object URLs
        return () => {
            Object.values(filePreviews).forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [filePreviews]);

    const handleFileDrop = (acceptedFiles, fieldName) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setFieldValue(fieldName, file); // Store the File object in Formik

            // Manage preview URL
            setFilePreviews(prev => {
                // Revoke previous URL if exists for this field
                if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                    URL.revokeObjectURL(prev[fieldName]);
                }
                return { ...prev, [fieldName]: URL.createObjectURL(file) };
            });
            const reader = new FileReader();
            reader.readAsDataURL(file);
                reader.onload = () => {
                    const imageDataUrl = reader.result;
                    const base64Image = imageDataUrl.split(',')[1]; // Get only the base64 part
                    dispatch(uploadCourseIcon({ ImageData: base64Image, folderName: 'Courses' })) // Corrected folderName
                        .unwrap()
                        .then((uploadedUrl) => {
                            // The useEffect listening to `uploadedImageUrl` will handle setting this URL
                            // to the formik field value and clearing `uploadedImageUrl` from redux state.
                        })
                        .catch(error => {
                            console.error("Image upload error in handleFileDrop:", error);
                            toast.error(t("ImageUploadFailed") + (error.message ? `: ${error.message}` : ''));
                            setFieldValue(fieldName, null); // Clear the file in Formik if upload failed
                            setFilePreviews(prev => { // Clear preview
                                if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                                    URL.revokeObjectURL(prev[fieldName]);
                                }
                                const newState = {...prev};
                                delete newState[fieldName];
                                return newState;
                            });
                        });
                };
                reader.onerror = (error) => {
                    console.error("FileReader error:", error);
                    toast.error(t("FileReadError"));
                    setFieldValue(fieldName, null); // Clear the file on reader error
                };
        }
    };

    const renderField = (item) => {
        const fieldName = item.name;
        const fieldValue = values[fieldName] ?? '';
        // Determine if field should be disabled
        const isDisabled = typeof item.disabled === 'function' ? item.disabled(isAddOrEdit, values) : !!item.disabled;

        switch (item.fieldName) {
            case 'text-box':
                return (
                    <Input
                        type="text"
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={(e) => {
                        const inputValue = e.target.value;
                        
                        // If it's RoleName field, allow only letters and spaces
                        if (item.name === "CourseNameEn" || item.name === "CourseNameAr" ) {
                            const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                            setFieldValue(item.name, cleanedValue);
                        } else {
                            handleChange(e);
                        }
                        }}
                        maxLength={item.maxLength} 
                        minLength={item.minLength}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'text-box-translate':
            case 'textarea-translate':
                const InputComponent = item.fieldName === 'textarea-translate' || item.isTextArea ? 'textarea' : 'text';

                return (
                    <div className={`input-group ${isRTL ? 'rtl-input-group' : ''}`}>
                        <Input
                            type={InputComponent === 'textarea' ? 'textarea' : 'text'}
                            rows={item.fieldName === 'textarea-translate' || item.isTextArea ? (item.rows || "2") : undefined}
                            id={fieldName}
                            name={fieldName}
                            placeholder={t(item.placeholder || item.labelName)}
                            value={fieldValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form-control"
                            invalid={touched[fieldName] && !!errors[fieldName]}
                            disabled={isDisabled}
                            required={item.required}
                        />
                    {onTranslateClickHandler && item.translateTo && (
                        <Button
                            type="button"
                            onClick={() => onTranslateClickHandler(item.translateTo, fieldValue, setFieldValue, item.name)}
                            disabled={!fieldValue || !fieldValue.trim() || translationLoading}
                        >
                            {translationLoading && lastTranslatedField === item.name ? (
                            <Spinner size="sm" />
                            ) : (
                            <i className="ri-translate"></i>
                            )}
                        </Button>
                    )}
                    </div>
                );

            case 'textarea-box':
                return (
                    <Input
                        type="textarea" // reactstrap Input uses this for textarea
                        rows={item.rows || "2"}
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'number-box':
                return (
                    <Input
                    type="text" 
                    id={fieldName}
                    name={fieldName}
                    placeholder={t(item.placeholder || item.labelName)}
                    value={fieldValue}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        let filteredValue = inputValue.replace(/[^0-9.]/g, '');

                        const parts = filteredValue.split('.');
                        if (parts.length > 2) {
                            filteredValue = parts[0] + '.' + parts.slice(1).join('');
                        }
                        setFieldValue(item.name, filteredValue);
                    }}
                    onBlur={handleBlur}
                    className="form-control"
                    invalid={touched[fieldName] && !!errors[fieldName]}
                    disabled={isDisabled}
                    required={item.required}
                    style={{ textAlign: isRTL ? 'right' : 'left' }}
                />
                );

            case 'check-box':
                return (
                    <div className="form-check mt-2"> {/* Added for better alignment */}
                        <Input
                            type="checkbox"
                            id={fieldName}
                            name={fieldName}
                            checked={!!fieldValue} // Formik value will be true/false
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form-check-input"
                            disabled={isDisabled}
                            required={item.required} // Required on checkbox might behave differently based on browser
                        />
                        <label className="form-check-label" htmlFor={fieldName}>
                            {/* Optional: if label needs to be next to checkbox */}
                            {/* {t(item.checkboxLabel || item.labelName)} */}
                        </label>
                    </div>
                );

            case 'date':
                return (
                    <Input
                        type="date"
                        id={fieldName}
                        name={fieldName}
                        min={item.minToday ? today : item.min}
                        max={item.max}
                        value={(fieldValue || '').split('T')[0]} // Ensure date format yyyy-mm-dd
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'referral-dropdown':
                return (
                    <Input
                        type="select"
                        id={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    >
                        <option value="">{t(item.defaultOptionLabel || "Choose")}</option>
                        {(item.options || ReferralReward || []).map((option) => (
                            <option
                                key={option[item.optionValueKey || 'ID']}
                                value={option[item.optionValueKey || 'ID']}
                            >
                                {i18n?.language === 'en'
                                    ? option[item.optionLabelKeyEn || 'ReferralNameEn']
                                    : option[item.optionLabelKeyAr || 'ReferralNameAr']}
                            </option>
                        ))}
                    </Input>
                );

            case 'day-multiselect-dropdown':
                const selectedDayValues = Days?.filter(day =>
                Array.isArray(values[fieldName]) && values[fieldName].includes(day.ID)
                ) || [];
                
                return (
                    <>
                        <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                            {(() => {
                                const isInvalid = touched[fieldName] && !!errors[fieldName];
                                const baseSearchBoxStyles = {
                                    paddingRight: i18n.language === 'ar' ? '30px' : '',
                                    textAlign: i18n.language === 'ar' ? 'right' : 'left',
                                };
                                if (isInvalid) {
                                    baseSearchBoxStyles.borderColor = '#dc3545'; // Bootstrap's danger color
                                }
                                const multiselectCustomStyles = {
                                    multiselectContainer: { textAlign: i18n.language === 'ar' ? 'right' : 'left', direction: i18n.language === 'ar' ? 'rtl' : 'ltr' },
                                    searchBox: baseSearchBoxStyles,
                                    chips: { direction: i18n.language === 'ar' ? 'rtl' : 'ltr', justifyContent: i18n.language === 'ar' ? 'flex-end' : 'flex-start' },
                                    optionContainer: { textAlign: i18n.language === 'ar' ? 'right' : 'left' },
                                };

                            return <Multiselect
                                key={`multiselect-days-${isAddOrEdit}-${values.ID || 'new'}`}
                                options={Days || []}
                                selectedValues={selectedDayValues}
                                onSelect={(selectedList, selectedItem) => {
                                    const currentValues = values[fieldName] || [];
                                    let newValues = currentValues;
                                    if (!currentValues.includes(selectedItem.ID)) {
                                        newValues = [...currentValues, selectedItem.ID];
                                    }
                                    setFieldValue(fieldName, newValues, true);
                                    setFieldTouched(fieldName, true, false);
                                }}
                                onRemove={(selectedList, removedItem) => {
                                    const currentValues = values[fieldName] || [];
                                    const newValues = currentValues.filter(id => id !== removedItem.ID);
                                    // Set field value and trigger validation (third argument true)
                                    setFieldValue(fieldName, newValues, true);
                                    // Mark field as touched.
                                    setFieldTouched(fieldName, true, false);
                                }}
                                displayValue={i18n.language === 'en' ? 'DayNameEn' : 'DayNameAr'}
                                placeholder={t(item.placeholder || "SelectDays")}
                                disable={isDisabled}
                                emptyRecordMsg={t("NoOptionAvailable")}
                                style={multiselectCustomStyles}
                            />
                            })()}
                        </div>
                        {/* Hidden input for Formik validation, if required attribute is needed */}
                        <input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={(values[fieldName] || []).length > 0 ? 'selected' : ''}
                            onChange={() => {}} // Dummy onchange, Formik handles value via setFieldValue
                            // 'required' here helps with native browser validation if Formik's default submit prevention is bypassed,
                            // but primary validation should come from Formik schema.
                            required={item.required}
                            style={{
                                opacity: 0,
                                height: 0,
                                width: 0,
                                position: 'absolute',
                                pointerEvents: 'none',
                            }}
                        />
                    </>
                );

            case 'file':
                    let previewSrc = null;
                    let uploadedFileName = '';
                    let uploadedFileSize = '';
                    const currentFile = values[fieldName];

                    if (typeof currentFile === 'string' && currentFile) { // Existing file URL
                        previewSrc = currentFile;
                        uploadedFileName = currentFile.substring(currentFile.lastIndexOf('/') + 1);
                    } else if (currentFile instanceof File) { // New file
                        previewSrc = filePreviews[fieldName]; // Use state for preview URL
                        uploadedFileName = currentFile.name;
                        uploadedFileSize = formatBytes(currentFile.size);
                    }

                    return (
                        <CardBody className="p-0"> {/* Adjusted padding */}
                            <Dropzone
                                onDrop={(acceptedFiles) => handleFileDrop(acceptedFiles, fieldName)}
                                disabled={isDisabled || imageLoad} // Corrected to use imageLoad from Courses slice
                                accept={item.accept}
                            >
                                {({ getRootProps, getInputProps }) => (
                                    <div 
                                        className="dropzone dz-clickable" 
                                        style={{ minHeight: '60px', position: 'relative' }}
                                    >
                                        <div className="dz-message needsclick" {...getRootProps()}>
                                            <input {...getInputProps()} />
                                            {imageLoad && ( // Corrected to use imageLoad
                                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'none' }}>
                                                    <Spinner size="lg" color="primary" />
                                                </div>
                                            )}
                                            <div style={{ opacity: imageLoad ? 0.5 : 1 }}> {/* Corrected to use imageLoad */}
                                                <h5>{t("DropFile")}</h5>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Dropzone>

                            {previewSrc && (
                                <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                                    <div className="p-2">
                                        <Row className="align-items-center">
                                            <Col className="col-auto">
                                                <img
                                                    data-dz-thumbnail=""
                                                    height="80"
                                                    className="avatar-sm rounded bg-light"
                                                    src={previewSrc}
                                                    alt={uploadedFileName || "preview"}
                                                />
                                            </Col>
                                            <Col>
                                                <Link to="#" className="text-muted font-weight-bold">
                                                    {uploadedFileName}
                                                </Link>
                                                {uploadedFileSize && <p className="mb-0"><strong>{uploadedFileSize}</strong></p>}
                                            </Col>
                                            {/* Optional: Button to clear the file */}
                                            {!isDisabled && (values[fieldName]) && (
                                                <Col className="col-auto">
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => {
                                                            setFieldValue(fieldName, null); // Clear Formik value
                                                            setFilePreviews(prev => {
                                                                if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                                                                    URL.revokeObjectURL(prev[fieldName]);
                                                                }
                                                                const newState = {...prev};
                                                                delete newState[fieldName];
                                                                return newState;
                                                            });
                                                        }}
                                                    >
                                                        <i className="ri-close-line"></i>
                                                    </Button>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                </Card>
                            )}
                        </CardBody>
                    );

            default:
                return <p>Unsupported field type: {item.fieldName}</p>;
        }
    };

    return (
        <Row>
            {labels.map((item) => {
                const fieldName = item.name;
                const showLabel = item.fieldName !== 'check-box' || !item.noMainLabel;

                return (
                    <Col sm={item.colSize || 6} key={fieldName} className={item.className}>
                        <div className="mb-3">
                            {showLabel && (
                                <label htmlFor={fieldName} className="col-form-label">
                                    {t(item.labelName)}
                                    {item.required && <span className="text-danger"> *</span>}
                                </label>
                            )}
                            {renderField(item)}
                            {touched[fieldName] && errors[fieldName] && (
                                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                                    {/* Ensure errors are translated if they are keys */}
                                    {typeof errors[fieldName] === 'string' ? t(errors[fieldName]) : errors[fieldName]}
                                </div>
                            )}
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export default AddCourseForm;